
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { LensInventory, LensFormValues } from "@/types/inventory";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { lensSchema } from "@/types/inventory";
import { toast } from "sonner";
import { ensureTablesExist } from "@/utils/databaseSetup";
import { SortDirection } from "@/components/inventory/SortButton";

const SORT_FIELD_KEY = "lensInventorySortField";
const SORT_DIRECTION_KEY = "lensInventorySortDirection";

export function useLensInventory() {
  const [lenses, setLenses] = useState<LensInventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tablesExist, setTablesExist] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  const [activeSort, setActiveSort] = useState<string | null>(
    sessionStorage.getItem(SORT_FIELD_KEY) || "brand"
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    (sessionStorage.getItem(SORT_DIRECTION_KEY) as SortDirection) || "asc"
  );

  const form = useForm<LensFormValues>({
    resolver: zodResolver(lensSchema),
    defaultValues: {
      lens_name: "",
      brand: "",
      mount: "",
      status: "working",
      serial_number: "",
      purchase_date: "",
      notes: "",
    },
  });

  useEffect(() => {
    if (activeSort) {
      sessionStorage.setItem(SORT_FIELD_KEY, activeSort);
    }
    if (sortDirection) {
      sessionStorage.setItem(SORT_DIRECTION_KEY, sortDirection);
    }
  }, [activeSort, sortDirection]);

  const checkTables = async () => {
    const exist = await ensureTablesExist();
    setTablesExist(exist);
    return exist;
  };

  const fetchLenses = async () => {
    try {
      setError(null);
      setLoading(true);
      
      const tablesExist = await checkTables();
      if (!tablesExist) {
        setLenses([]);
        setError('Database tables not set up properly. Please contact your administrator.');
        return;
      }
      
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        throw sessionError;
      }
      
      if (!sessionData.session?.user) {
        throw new Error('You must be signed in to view your lenses');
      }
      
      const currentUserId = sessionData.session.user.id;
      
      let query = supabase.from('lens_inventory' as any)
        .select('*')
        .eq('user_id', currentUserId);
      
      if (activeSort && sortDirection) {
        query = query.order(activeSort, { ascending: sortDirection === 'asc' });
      } else {
        query = query.order('created_at', { ascending: false });
      }
      
      const { data, error } = await query;

      if (error) {
        throw error;
      }
      
      if (Array.isArray(data)) {
        if (data.length > 0) {
          try {
            setLenses(data as unknown as LensInventory[]);
          } catch (err) {
            console.error("Data validation error:", err);
            setError("Received invalid data format from the server");
            setLenses([]);
          }
        } else {
          setLenses([]);
        }
      } else {
        setLenses([]);
      }
    } catch (err: any) {
      console.error('Error fetching lenses:', err);
      setError(err.message || 'Failed to load lenses. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (value: string) => {
    setActiveSort(value);
    if (!sortDirection) {
      setSortDirection('asc');
    }
  };

  const handleDirectionChange = () => {
    if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortDirection('asc');
    }
  };

  const handleAddLens = async (values: LensFormValues) => {
    try {
      if (!tablesExist) {
        toast.error('Database tables not set up. Cannot add lenses.');
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      
      if (!userId) {
        toast.error('You must be signed in to add lenses');
        return;
      }

      console.log("Submitting lens with values:", values);

      const mountValue = values.mount.toUpperCase();

      const query = supabase.from('lens_inventory' as any)
        .insert({
          lens_name: values.lens_name,
          brand: values.brand,
          mount: mountValue,
          status: values.status,
          serial_number: values.serial_number || null,
          purchase_date: values.purchase_date || null,
          purchase_price: values.purchase_price || null,
          notes: values.notes || null,
          user_id: userId,
        });

      const { error, data } = await query;

      if (error) {
        console.error("Error details:", error);
        throw error;
      }
      
      toast.success('Lens added to inventory');
      form.reset();
      setIsAddDialogOpen(false);
      fetchLenses();
    } catch (error: any) {
      console.error('Error details:', error);
      toast.error(`Error adding lens: ${error.message}`);
    }
  };

  useEffect(() => {
    fetchLenses();
  }, [activeSort, sortDirection]);

  const filteredLenses = searchQuery 
    ? lenses.filter(lens => 
        lens.lens_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lens.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lens.mount.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : lenses;

  return {
    lenses: filteredLenses,
    loading,
    error,
    tablesExist,
    isAddDialogOpen,
    setIsAddDialogOpen,
    searchQuery,
    setSearchQuery,
    activeSort,
    sortDirection,
    handleSortChange,
    handleDirectionChange,
    form,
    handleAddLens,
    fetchLenses
  };
}
